(function () {
  'use strict';

  const theme = window.HouseOfThiraa;
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const padIndex = (value) => String(value).padStart(2, '0');

  class ProductPage {
    constructor(section) {
      this.section = section;
      this.form = section.querySelector('[data-product-form]');
      this.variantInput = section.querySelector('[data-variant-id]');
      this.variants = this.readVariants();
      this.addButton = section.querySelector('[data-add-to-cart]');
      this.addLabel = section.querySelector('[data-add-to-cart-label]');
      this.price = section.querySelector('[data-price]');
      this.priceRegular = section.querySelector('[data-price-regular]');
      this.priceCompare = section.querySelector('[data-price-compare]');
      this.unitPrice = section.querySelector('[data-unit-price]');
      this.optionError = section.querySelector('[data-option-error]');
      this.quantity = section.querySelector('[data-quantity]');
      this.addArrow = section.querySelector('[data-add-arrow]');
      this.dynamicCheckout = section.querySelector('[data-dynamic-checkout]');
      this.inventory = section.querySelector('[data-inventory]');
      this.pickup = section.querySelector('[data-pickup]');
      this.pickupBody = section.querySelector('[data-pickup-body]');
      this.installments = section.querySelector('[data-installments]');
      this.quantityInput = section.querySelector('input[name="quantity"]');
      this.productAvailable = section.dataset.productAvailable !== 'false';
      this.viewport = section.querySelector('[data-gallery-viewport]');
      this.slides = Array.from(section.querySelectorAll('.product-page__slide'));
      this.thumbs = Array.from(section.querySelectorAll('[data-thumb]'));
      this.currentLabel = section.querySelector('[data-gallery-current]');
      this.indexLabel = section.querySelector('[data-gallery-index]');
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.lastFocus = null;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      section.addEventListener('click', this.onClick);
      this.form?.addEventListener('change', () => this.onChange());
      this.form?.addEventListener('submit', this.onSubmit);
      this.dynamicCheckout?.addEventListener('click', (event) => this.onDynamicCheckoutClick(event), true);
      this.viewport?.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });
      document.addEventListener('keydown', this.onKeydown);

      theme?.wishlist?.syncButtons(section);
      this.onChange();
      this.goTo(this.activeIndex, false);
      this.bindDescriptionToggle();
    }

    readVariants() {
      const source = this.section.querySelector('[data-product-json]');

      if (!source) {
        return [];
      }

      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return [];
      }
    }

    bindDescriptionToggle() {
      const root = this.section.querySelector('[data-product-description]');
      const toggle = root?.querySelector('[data-description-toggle]');

      if (!root || !toggle) {
        return;
      }

      toggle.addEventListener('click', () => {
        const accordionId = toggle.dataset.accordionTarget;

        if (accordionId) {
          const trigger = document.getElementById(accordionId);

          if (trigger) {
            if (trigger.getAttribute('aria-expanded') !== 'true') {
              trigger.click();
            }

            trigger.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }

          return;
        }

        const excerpt = root.querySelector('[data-description-excerpt]');
        const full = root.querySelector('[data-description-full]');
        const expanded = root.classList.toggle('is-expanded');

        root.classList.toggle('is-collapsed', !expanded);

        if (excerpt) {
          excerpt.hidden = expanded;
        }

        if (full) {
          full.hidden = !expanded;
        }

        toggle.textContent = expanded
          ? (toggle.dataset.lessLabel || 'Read less')
          : (toggle.dataset.moreLabel || 'Read more');
      });
    }

    normalizeSize(value) {
      const key = String(value || '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');

      if (key === 'XXL' || key === '2XL' || key === 'XXLARGE') {
        return '2XL';
      }

      return key;
    }

    optionValueMatches(variantValue, inputValue, isSize) {
      if (isSize) {
        return this.normalizeSize(variantValue) === this.normalizeSize(inputValue);
      }

      return String(variantValue) === String(inputValue);
    }

    selectedOptions() {
      if (!this.form) {
        return [];
      }

      return Array.from(this.form.querySelectorAll('[data-option-index]'))
        .filter((input) => input.checked && !input.disabled)
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((input) => input.value);
    }

    requiredOptionCount() {
      if (!this.form) {
        return 0;
      }

      return new Set(
        Array.from(this.form.querySelectorAll('[data-option-index]')).map((input) => input.dataset.optionIndex)
      ).size;
    }

    findVariant() {
      const options = this.selectedOptions();
      const selectedId = Number(this.variantInput?.value);

      if (this.requiredOptionCount() === 0) {
        return this.variants.find((variant) => Number(variant.id) === selectedId)
          || this.variants[0]
          || null;
      }

      if (options.length === this.requiredOptionCount()) {
        const matched = this.variants.find((variant) =>
          options.every((value, index) => String(variant.options[index]) === String(value))
        );

        if (matched) {
          return matched;
        }
      }

      return this.variants.find((variant) => Number(variant.id) === selectedId) || null;
    }

    isPurchasable(variant) {
      return Boolean(this.productAvailable && variant?.available);
    }

    updatePurchaseState(variant) {
      const purchasable = this.isPurchasable(variant);

      if (this.addButton) {
        this.addButton.disabled = !purchasable;
        const label = purchasable
          ? (this.addButton.dataset.addLabel || 'Add to bag')
          : (this.addButton.dataset.soldOutLabel || 'Sold out');

        if (this.addLabel) {
          this.addLabel.textContent = label;
        }
      }

      if (this.addArrow) {
        this.addArrow.hidden = !purchasable;
      }

      if (this.dynamicCheckout) {
        this.dynamicCheckout.hidden = !purchasable;
        this.dynamicCheckout.querySelectorAll('button, input').forEach((control) => {
          control.disabled = !purchasable;
        });
      }

      if (this.quantity) {
        this.quantity.hidden = !purchasable;
        this.quantity.querySelectorAll('input, button').forEach((control) => {
          control.disabled = !purchasable;
        });
      }

      this.updateInventory(variant, purchasable);
    }

    updateInventory(variant, purchasable) {
      if (!this.inventory) {
        return;
      }

      const soldOutLabel = this.inventory.dataset.soldOutLabel || this.addButton?.dataset.soldOutLabel || 'Sold out';

      if (!purchasable) {
        this.inventory.hidden = false;
        this.inventory.textContent = soldOutLabel;
        return;
      }

      const quantity = Number(variant?.inventory_quantity);
      const tracked = String(variant?.inventory_management || '') === 'shopify';
      const policy = String(variant?.inventory_policy || 'deny');
      const showLowStock = Boolean(
        tracked &&
        policy !== 'continue' &&
        Number.isFinite(quantity) &&
        quantity > 0 &&
        quantity <= 10
      );

      this.inventory.hidden = !showLowStock;

      if (!showLowStock) {
        this.inventory.textContent = '';
        return;
      }

      const template = this.inventory.dataset.lowStockTemplate || 'Only __COUNT__ left';
      this.inventory.textContent = template.replace(/__COUNT__/g, String(quantity));
    }

    updateQuantityLimit(variant) {
      if (!this.quantityInput) {
        return;
      }

      const quantity = Number(variant?.inventory_quantity);
      const tracked = String(variant?.inventory_management || '') === 'shopify';
      const policy = String(variant?.inventory_policy || 'deny');

      if (tracked && policy !== 'continue' && Number.isFinite(quantity) && quantity > 0) {
        this.quantityInput.max = String(quantity);
        if (Number(this.quantityInput.value) > quantity) {
          this.quantityInput.value = String(quantity);
        }
      } else {
        this.quantityInput.removeAttribute('max');
      }
    }

    updatePickup(variant) {
      if (!this.pickup || !this.pickupBody) {
        return;
      }

      const locations = (variant?.store_availabilities || []).filter((item) => item?.pick_up_enabled);
      const available = locations.filter((item) => item.available !== false);
      const availableLabel = this.pickup.dataset.availableLabel || 'Pickup available';
      const unavailableLabel = this.pickup.dataset.unavailableLabel || 'Pickup unavailable';

      if (!locations.length) {
        this.pickup.hidden = true;
        this.pickupBody.replaceChildren();
        return;
      }

      this.pickup.hidden = false;

      const status = document.createElement('p');
      status.setAttribute('data-pickup-status', '');

      if (!available.length) {
        status.textContent = unavailableLabel;
        this.pickupBody.replaceChildren(status);
        return;
      }

      status.textContent = availableLabel;
      const list = document.createElement('ul');
      list.setAttribute('data-pickup-locations', '');

      available.forEach((item) => {
        const row = document.createElement('li');
        const name = document.createElement('span');
        name.setAttribute('data-pickup-location', '');
        name.textContent = item.location_name || '';
        row.append(name);

        if (item.pick_up_time) {
          const time = document.createElement('span');
          time.setAttribute('data-pickup-time', '');
          time.textContent = item.pick_up_time;
          row.append(time);
        }

        list.append(row);
      });

      this.pickupBody.replaceChildren(status, list);
    }

    onChange() {
      if (this.isSyncingOptions) {
        return;
      }

      this.isSyncingOptions = true;
      this.syncOptionAvailability();

      const variant = this.findVariant();

      if (variant && this.variantInput) {
        this.variantInput.value = variant.id;
        this.updateVariantUrl(variant.id);
      }

      this.updatePurchaseState(variant);
      this.updatePrice(variant);
      this.updatePickup(variant);
      this.updateQuantityLimit(variant);
      this.updateSelectedOptionLabels();

      if (variant?.featured_media_id) {
        const slide = this.slides.find((item) => item.dataset.mediaId === String(variant.featured_media_id));
        if (slide) {
          this.goTo(Number(slide.dataset.index), true);
        }
      }

      if (variant && this.optionError) {
        this.optionError.hidden = true;
      }

      this.isSyncingOptions = false;
    }

    setOptionControlState(input, { exists, purchasable, isSize }) {
      const label = input.closest('.product-form__option-value');
      const available = Boolean(exists && purchasable);

      input.disabled = !available;
      input.setAttribute('aria-disabled', available ? 'false' : 'true');

      if (!available) {
        input.checked = false;
        input.tabIndex = -1;
        label?.setAttribute('aria-disabled', 'true');
        label?.setAttribute('tabindex', '0');
      } else {
        input.removeAttribute('tabindex');
        label?.removeAttribute('aria-disabled');
        label?.removeAttribute('tabindex');
      }

      label?.classList.toggle('is-unavailable', Boolean(exists && !purchasable));
      label?.classList.toggle('is-disabled', Boolean(!exists && isSize));
      label?.classList.toggle('is-invalid', Boolean(!exists && !isSize));
    }

    syncOptionAvailability() {
      if (!this.form || this.variants.length === 0) {
        return;
      }

      const selected = {};

      this.form.querySelectorAll('[data-option-index]:checked').forEach((input) => {
        if (input.disabled) {
          return;
        }

        selected[Number(input.dataset.optionIndex) - 1] = input.value;
      });

      this.form.querySelectorAll('[data-option-index]').forEach((input) => {
        const index = Number(input.dataset.optionIndex) - 1;
        const isSize = Boolean(input.dataset.sizeCanonical || input.closest('[data-option-kind="size"]'));
        const compareValue = input.dataset.sizeCanonical || input.value;
        const matchesOtherOptions = (variant) =>
          Object.entries(selected).every(([optionIndex, value]) => {
            if (Number(optionIndex) === index) {
              return true;
            }

            const otherInput = this.form.querySelector(
              `[data-option-index="${Number(optionIndex) + 1}"]:checked`
            );
            const otherIsSize = Boolean(
              otherInput?.dataset.sizeCanonical || otherInput?.closest('[data-option-kind="size"]')
            );

            return this.optionValueMatches(variant.options[Number(optionIndex)], value, otherIsSize);
          });

        const matching = this.variants.filter(
          (variant) =>
            this.optionValueMatches(variant.options[index], compareValue, isSize) &&
            matchesOtherOptions(variant)
        );
        const optionExists = matching.length > 0;
        const optionPurchasable = matching.some((variant) => variant.available);

        if (optionExists && matching[0]?.options[index]) {
          input.value = String(matching[0].options[index]);
        }

        this.setOptionControlState(input, {
          exists: optionExists,
          purchasable: optionPurchasable,
          isSize
        });
      });

      const sizeInputs = Array.from(this.form.querySelectorAll('[data-option-kind="size"] [data-option-index]'));
      if (sizeInputs.length && !sizeInputs.some((input) => input.checked && !input.disabled)) {
        const firstAvailable = sizeInputs.find((input) => !input.disabled);
        if (firstAvailable) {
          firstAvailable.checked = true;
        }
      }
    }

    updatePrice(variant) {
      if (!variant || !this.priceRegular || !variant.price) {
        return;
      }

      this.priceRegular.textContent = variant.price;
      if (Number.isFinite(Number(variant.price_cents))) {
        this.priceRegular.dataset.priceCents = String(variant.price_cents);
      }

      if (this.priceCompare) {
        if (variant.compare_at_price) {
          this.priceCompare.textContent = variant.compare_at_price;
          this.priceCompare.hidden = false;
        } else {
          this.priceCompare.hidden = true;
        }
      }

      if (this.unitPrice) {
        if (variant.unit_price) {
          this.unitPrice.textContent = variant.unit_price;
          this.unitPrice.hidden = false;
        } else {
          this.unitPrice.hidden = true;
        }
      }

      this.price?.classList.toggle('price--on-sale', Boolean(variant.compare_at_price));
    }

    updateSelectedOptionLabels() {
      this.form?.querySelectorAll('[data-option-kind]').forEach((fieldset) => {
        const selected = fieldset.querySelector('[data-option-selected]');
        const value = fieldset.querySelector('[data-option-index]:checked, input:checked');

        if (selected && value) {
          selected.textContent = value.value;
        }
      });
    }

    updateVariantUrl(variantId) {
      if (!variantId || !window.history.replaceState) {
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    goTo(index, scroll) {
      if (this.slides.length === 0) {
        return;
      }

      const nextIndex = Math.max(0, Math.min(this.slides.length - 1, index));
      this.activeIndex = nextIndex;

      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === nextIndex);
      });

      this.thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === nextIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (this.currentLabel) {
        this.currentLabel.textContent = padIndex(nextIndex + 1);
      }

      if (this.indexLabel) {
        this.indexLabel.textContent = padIndex(nextIndex + 1);
      }

      if (scroll !== false) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.slides[nextIndex]?.scrollIntoView({
          inline: 'start',
          block: 'nearest',
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
    }

    syncFromScroll() {
      if (!this.viewport || this.slides.length === 0) {
        return;
      }

      const index = Math.round(this.viewport.scrollLeft / Math.max(this.viewport.clientWidth, 1));

      if (index !== this.activeIndex) {
        this.goTo(index, false);
      }
    }

    onClick(event) {
      const unavailableOption = event.target.closest(
        '.product-form__option-value.is-disabled, .product-form__option-value.is-unavailable, .product-form__option-value.is-invalid'
      );

      if (unavailableOption) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const thumb = event.target.closest('[data-thumb]');
      const prev = event.target.closest('[data-gallery-prev]');
      const next = event.target.closest('[data-gallery-next]');
      const qty = event.target.closest('[data-qty]');
      const wishlist = event.target.closest('[data-featured-products-wishlist]');
      const openGuide = event.target.closest('[data-size-guide-open]');
      const closeGuide = event.target.closest('[data-size-guide-close]');

      if (thumb) {
        this.goTo(Number(thumb.dataset.index), true);
      }

      if (prev) {
        this.goTo(this.activeIndex - 1, true);
      }

      if (next) {
        this.goTo(this.activeIndex + 1, true);
      }

      if (qty) {
        if (!this.isPurchasable(this.findVariant())) {
          return;
        }

        const input = this.section.querySelector('input[name="quantity"]');
        if (input && !input.disabled) {
          const nextValue = Math.max(1, Number(input.value || 1) + Number(qty.dataset.qty));
          const max = Number(input.max);
          input.value = String(Number.isFinite(max) && max > 0 ? Math.min(max, nextValue) : nextValue);
        }
      }

      if (wishlist) {
        event.preventDefault();
        theme?.wishlist?.toggle(wishlist.dataset.productId, wishlist.dataset.productHandle);
      }

      if (openGuide) {
        event.preventDefault();
        this.openSizeGuide(openGuide);
      }

      if (closeGuide) {
        event.preventDefault();
        this.closeSizeGuide();
      }
    }

    onDynamicCheckoutClick(event) {
      const variant = this.findVariant();

      if (!variant) {
        event.preventDefault();
        event.stopPropagation();
        this.showOptionError();
        return;
      }

      if (!this.isPurchasable(variant)) {
        event.preventDefault();
        event.stopPropagation();
        this.updatePurchaseState(variant);
      }
    }

    async onSubmit(event) {
      const variant = this.findVariant();
      const submitter = event.submitter;
      const isAddToBag = !submitter || submitter.getAttribute('name') === 'add';

      if (!variant) {
        event.preventDefault();
        this.showOptionError();
        return;
      }

      if (!this.isPurchasable(variant)) {
        event.preventDefault();
        this.updatePurchaseState(variant);
        return;
      }

      if (!isAddToBag) {
        return;
      }

      event.preventDefault();

      const root = window.Shopify?.routes?.root || '/';
      const quantity = Number(this.form.querySelector('input[name="quantity"]')?.value || 1);
      const defaultLabel = this.addButton?.dataset.addLabel || 'Add to bag';

      if (this.addButton) {
        this.addButton.disabled = true;
        if (this.addLabel) {
          this.addLabel.textContent = this.addButton.dataset.addingLabel || 'Adding...';
        }
      }

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: Number(variant.id),
            quantity: Number.isNaN(quantity) ? 1 : quantity
          })
        });

        if (!response.ok) {
          throw new Error('Unable to add item to cart.');
        }

        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;

        if (theme?.events && cart) {
          theme.events.emit('cart:updated', {
            cart,
            itemCount: cart.item_count,
            item_count: cart.item_count
          });
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart, itemCount: cart?.item_count, item_count: cart?.item_count }
        }));

        theme?.a11y?.announce(this.addButton?.dataset.addedLabel || 'Added to bag');

        if (this.addLabel) {
          this.addLabel.textContent = this.addButton.dataset.addedLabel || 'Added to bag';
        }

        window.setTimeout(() => {
          if (this.addButton && this.addLabel) {
            this.addButton.disabled = false;
            this.addLabel.textContent = defaultLabel;
          }
        }, 1400);
      } catch (error) {
        this.form.submit();
      }
    }

    showOptionError() {
      if (!this.optionError) {
        return;
      }

      this.optionError.hidden = false;
    }

    openSizeGuide(trigger) {
      const guide = this.section.querySelector('[data-size-guide]');
      const panel = this.section.querySelector('[data-size-guide-panel]');

      if (!guide || !panel) {
        return;
      }

      this.lastFocus = trigger || document.activeElement;
      this.guideTrigger = trigger || this.section.querySelector('[data-size-guide-open]');
      this.guideTrigger?.setAttribute('aria-expanded', 'true');
      guide.hidden = false;
      document.body.classList.add('product-size-guide-open');
      window.requestAnimationFrame(() => {
        const closeButton = panel.querySelector('[data-size-guide-close]');
        (closeButton || panel).focus();
      });
    }

    closeSizeGuide() {
      const guide = this.section.querySelector('[data-size-guide]');

      if (!guide || guide.hidden) {
        return;
      }

      guide.hidden = true;
      document.body.classList.remove('product-size-guide-open');
      this.guideTrigger?.setAttribute('aria-expanded', 'false');
      (this.lastFocus || this.guideTrigger)?.focus();
    }

    onKeydown(event) {
      const blockedOption = event.target.closest?.(
        '.product-form__option-value.is-disabled, .product-form__option-value.is-unavailable, .product-form__option-value.is-invalid'
      );

      if (blockedOption && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        return;
      }

      const guide = this.section.querySelector('[data-size-guide]');
      const panel = this.section.querySelector('[data-size-guide-panel]');
      const isOpen = Boolean(guide && !guide.hidden);

      if (!isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        this.closeSizeGuide();
        return;
      }

      if (event.key !== 'Tab' || !panel) {
        return;
      }

      const focusable = Array.from(panel.querySelectorAll(focusableSelector)).filter((node) => {
        return !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true';
      });

      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const accordionPanel = (trigger) => {
    const panelId = trigger.getAttribute('aria-controls');
    if (!panelId) {
      return null;
    }

    const root = trigger.closest('[data-product-accordions]');
    if (root) {
      return Array.from(root.querySelectorAll('[data-accordion-panel]')).find((panel) => panel.id === panelId) || null;
    }

    return document.getElementById(panelId);
  };

  const setAccordionState = (trigger, open) => {
    const panel = accordionPanel(trigger);
    const icon = trigger.querySelector('[data-accordion-icon]');

    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (icon) {
      icon.textContent = open ? '–' : '+';
    }
    if (panel) {
      panel.classList.toggle('is-open', open);
      if (open) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    }
  };

  const toggleProductAccordion = (trigger, root) => {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    root.querySelectorAll('[data-accordion-trigger]').forEach((button) => {
      setAccordionState(button, false);
    });

    if (!isOpen) {
      setAccordionState(trigger, true);
    }
  };

  const bindProductAccordions = (scope = document) => {
    scope.querySelectorAll('[data-product-accordions]').forEach((root) => {
      if (root.dataset.accordionBound === 'true') {
        return;
      }

      root.dataset.accordionBound = 'true';
      root.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
        setAccordionState(trigger, false);
      });

      root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-accordion-trigger]');

        if (!trigger || !root.contains(trigger)) {
          return;
        }

        event.preventDefault();
        toggleProductAccordion(trigger, root);
      });
    });

    if (document.documentElement.dataset.productAccordionEscape !== 'true') {
      document.documentElement.dataset.productAccordionEscape = 'true';
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        document.querySelectorAll('[data-product-accordions] [data-accordion-trigger]').forEach((button) => {
          setAccordionState(button, false);
        });
      });
    }
  };

  const initProducts = (scope = document) => {
    scope.querySelectorAll('[data-product]').forEach((section) => {
      if (section.dataset.productInitialized === 'true') {
        return;
      }

      section.dataset.productInitialized = 'true';

      try {
        new ProductPage(section);
      } catch (error) {
        // Keep the native product form usable if enhancement fails.
      }
    });
  };

  const loadRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-product-recommendations]').forEach(async (root) => {
      if (root.dataset.recommendationsLoaded === 'true' || !root.dataset.url) {
        return;
      }

      if (root.querySelector('[data-product-card], .featured-product-card')) {
        root.dataset.recommendationsLoaded = 'true';
        return;
      }

      try {
        const response = await fetch(root.dataset.url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-product-recommendations]');

        if (next) {
          root.innerHTML = next.innerHTML;
        }

        root.dataset.recommendationsLoaded = 'true';
        theme?.wishlist?.syncButtons(root);

        const look = root.closest('[data-complete-look]');
        if (look) {
          look.hidden = !root.querySelector('[data-product-card], .featured-product-card');
        }
      } catch (error) {
        root.dataset.recommendationsLoaded = 'true';
        const look = root.closest('[data-complete-look]');
        if (look) {
          look.hidden = true;
        }
      }
    });
  };

  const bindRecommendationWishlist = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-product-recommendations] [data-featured-products-wishlist]');

      if (!button) {
        return;
      }

      event.preventDefault();
      theme?.wishlist?.toggle(button.dataset.productId, button.dataset.productHandle);
    });
  };

  bindProductAccordions();
  initProducts();
  loadRecommendations();
  bindRecommendationWishlist();
  theme?.events?.on('section:load', (event) => {
    initProducts(event.detail.target);
    bindProductAccordions(event.detail.target);
    loadRecommendations(event.detail.target);
  });
})();
